import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { useUserEducations, UserEducation } from "@/hooks/useUserEducations";
import { EducationForm } from "./EducationForm";
import { 
  Edit3, Plus, GraduationCap, Calendar, 
  Award, Trash2
} from "lucide-react";


interface EducationSectionProps {
  userId: string;
  isEditing: boolean;
}

export const EducationSection = ({ userId, isEditing }: EducationSectionProps) => {
  const { t, i18n } = useTranslation('ui');
  const toast = useTranslatedToast();
  const { educations, loading, addEducation, updateEducation, deleteEducation } = useUserEducations(userId);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [formData, setFormData] = useState({
    institution: "",
    degree: "",
    field_of_study: "",
    start_date: "",
    end_date: "",
    is_current: false,
    grade: "",
    activities: "",
    description: ""
  });

  const handleSave = useCallback(async (educationId?: string) => {
    try {
      const dataToSave = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        field_of_study: formData.field_of_study || null,
        grade: formData.grade || null,
        activities: formData.activities || null,
        description: formData.description || null
      };

      if (educationId) {
        await updateEducation(educationId, dataToSave);
      } else {
        await addEducation(dataToSave);
        setShowAddForm(false);
      }

      setEditingItem(null);
      resetForm();
      toast.success('success.updated');
    } catch (err) {
      console.error('Error saving education:', err);
      toast.error('error.failed_to_save');
    }
  }, [formData, updateEducation, addEducation, toast]);

  const handleDelete = useCallback(async (educationId: string) => {
    try {
      await deleteEducation(educationId);
      toast.success('success.deleted');
    } catch (err) {
      console.error('Error deleting education:', err);
      toast.error('error.generic');
    }
  }, [deleteEducation, toast]);

  const resetForm = useCallback(() => {
    setFormData({
      institution: "",
      degree: "",
      field_of_study: "",
      start_date: "",
      end_date: "",
      is_current: false,
      grade: "",
      activities: "",
      description: ""
    });
  }, []);

  const loadFormData = useCallback((education: UserEducation) => {
    setFormData({
      institution: education.institution,
      degree: education.degree,
      field_of_study: education.field_of_study || "",
      start_date: education.start_date || "",
      end_date: education.end_date || "",
      is_current: education.is_current,
      grade: education.grade || "",
      activities: education.activities || "",
      description: education.description || ""
    });
  }, []);

  const handleFormDataChange = useCallback((field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    setEditingItem(null);
    setShowAddForm(false);
    resetForm();
  }, [resetForm]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    
    // 根据当前语言设置本地化格式
    const languageMap: { [key: string]: string } = {
      'en': 'en-US',
      'es': 'es-ES', 
      'zh': 'zh-CN',
      'fr': 'fr-FR',
      'ja': 'ja-JP'
    };
    
    const locale = languageMap[i18n.language] || 'en-US';
    
    return new Date(dateString).toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'short' 
    });
  };


  if (loading) {
    return (
      <div className="text-center py-8">{t('profile_sections.loading_education')}</div>
    );
  }

  return (
    <div className="space-y-6">
      {isEditing && !showAddForm && (
        <div className="flex justify-end">
          <Button onClick={() => setShowAddForm(true)} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            {t('profile_sections.add_education')}
          </Button>
        </div>
      )}
        {showAddForm && (
          <EducationForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}

        {educations.length === 0 && !showAddForm ? (
          <div className="py-12 text-center">
            <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t('profile_sections.no_education_title')}</h3>
            <p className="text-muted-foreground text-sm">
              {t('profile_sections.no_education_description')}
            </p>
          </div>
        ) : (
          educations.map((education) => (
            <div key={education.id} className="border-b pb-6 last:border-b-0">
              {editingItem === education.id ? (
                <EducationForm
                  formData={formData}
                  onFormDataChange={handleFormDataChange}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  educationId={education.id}
                />
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Institution name as primary heading */}
                      <h3 className="text-lg font-bold text-foreground mb-1">
                        {education.institution}
                      </h3>
                      
                      {/* Degree as secondary text */}
                      <p className="text-base text-muted-foreground mb-3">
                        {education.degree}
                        {education.field_of_study && ` in ${education.field_of_study}`}
                      </p>
                      
                      {/* Tags for dates and grade */}
                      <div className="flex items-center flex-wrap gap-2 text-xs mb-4">
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {formatDate(education.start_date)} - {
                            education.is_current ? t('profile_sections.present') : formatDate(education.end_date)
                          }
                        </span>
                        {education.grade && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            <Award className="w-3 h-3" />
                            {education.grade}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {isEditing && (
                      <div className="flex gap-2 ml-4">
                        <Button 
                          onClick={() => {
                            loadFormData(education);
                            setEditingItem(education.id);
                          }} 
                          size="sm" 
                          variant="ghost"
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button 
                          onClick={() => handleDelete(education.id)} 
                          size="sm" 
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Activities and description with proper typography */}
                  {education.activities && (
                    <div className="space-y-2">
                      <p className="text-sm text-foreground">
                        <span className="font-medium">{t('profile_sections.activities')}:</span> {education.activities}
                      </p>
                    </div>
                  )}

                  {education.description && (
                    <div className="space-y-2">
                      <p className="text-sm text-foreground leading-relaxed">
                        {education.description}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
    </div>
  );
};